import { useEffect, useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Search, LogOut, User, Wallet, Copy, Check, Loader2, CreditCard, ArrowUpFromLine, Lightbulb, ChevronRight, Coins, RotateCw, Shield, Building2 } from "lucide-react";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Avatar } from "@/components/ui/Avatar";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { NetworkSwitcher } from "@/components/ui/NetworkSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useAppLogout, useMockAuth } from "@/hooks/useAppAuth";
import { useWalletBalances } from "@/hooks/useWalletBalances";
import { useMintTestUsdt } from "@/hooks/useMintTestUsdt";
import { useNetworkStore } from "@/stores/useNetworkStore";
import { isMainnetChainId } from "@/config/activeChain";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useNotifications } from "@/hooks/useNotifications";
import { FundWalletModal, type Flow } from "@/features/ramp";
import { FEEDBACK_URL, ROUTES } from "@/lib/constants";
import { useLocale } from "@/hooks/useLocale";
import { useNotificationChannels } from "@/features/channels/hooks/useNotificationChannels";
import { getActiveChannels } from "@/features/channels/channelManifest";
import { useTelegramHandoff } from "@/features/channels/TelegramConnect";
import { useProfile } from "@/hooks/useProfile";
import { TrackRecordMini } from "@/features/profile/trackRecord/TrackRecordMini";
import {
  GettingStartedRow,
  GettingStartedSection,
  ProgressRing,
  useWorkerChecklist,
  type Checklist,
} from "@/features/gettingStarted";
import { HOVER_CLOSE_MS, OrgChecklistPill } from "@/features/organizations/gettingStarted/OrgChecklistRing";
import { ShellBackButton } from "./ShellBackButton";

function formatUsd(amount: number, locale = "en-US"): string {
  return amount.toLocaleString(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function MockAvatarMenu({ checklist }: { checklist: Checklist | null }) {
  const navigate = useNavigate();
  const { session, displayName } = useAuth();
  const { logoutMock } = useAppLogout();

  return (
    <AvatarDropdown
      displayName={displayName}
      avatarUrl={session?.avatarUrl}
      walletAddress={session?.walletAddress}
      checklist={checklist}
      onProfile={() => navigate(ROUTES.PROFILE)}
      onLogout={logoutMock}
    />
  );
}

function PrivyAvatarMenu({ checklist }: { checklist: Checklist | null }) {
  const navigate = useNavigate();
  const { session, displayName } = useAuth();
  const { logout } = usePrivy();

  return (
    <AvatarDropdown
      displayName={displayName}
      avatarUrl={session?.avatarUrl}
      walletAddress={session?.walletAddress}
      checklist={checklist}
      onProfile={() => navigate(ROUTES.PROFILE)}
      onLogout={async () => {
        await logout();
        navigate("/");
      }}
    />
  );
}

function BalanceSummary({ isTestnet }: { isTestnet: boolean }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data, isPending, refetch, isFetching } = useWalletBalances();

  if (isPending) {
    return (
      <div className="flex h-[104px] items-center justify-center" role="status">
        <Loader2 className="h-5 w-5 motion-safe:animate-spin text-white/85" aria-hidden />
        <span className="sr-only">{t("topbar.loadingBalances")}</span>
      </div>
    );
  }

  // A failed read (a wrong RPC, a token contract missing on this chain) used
  // to render nothing, leaving a card with a label and no numbers. Say so and
  // offer a retry. An item, not a button, so arrow keys reach it; preventDefault
  // keeps the menu open for the result.
  if (!data) {
    return (
      <div className="flex min-h-[104px] flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm font-medium text-white">{t("topbar.balancesFailed")}</p>
        <DropdownMenu.Item
          onSelect={(e) => {
            e.preventDefault();
            void refetch();
          }}
          disabled={isFetching}
          className="flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg bg-white/10 px-3 text-sm font-semibold text-white outline-none ring-1 ring-inset ring-white/40 transition hover:bg-white/20 focus:bg-white/20 focus-visible:ring-2 focus-visible:ring-white data-[disabled]:cursor-wait data-[disabled]:opacity-70"
        >
          <RotateCw className={`h-4 w-4 ${isFetching ? "motion-safe:animate-spin" : ""}`} aria-hidden />
          {t("topbar.retry")}
        </DropdownMenu.Item>
      </div>
    );
  }

  const polUsd =
    data.polPriceUsd != null ? data.pol * data.polPriceUsd : null;

  // Text on the card stays at white/90 or above: on the gradient's lightest
  // stop (brand-700) that keeps small text at or near AAA 7:1.
  // Sizes are rem, not px: px ignores the phone's "larger text" setting.
  return (
    <>
      <div className="mt-1.5 text-[1.75rem] font-bold leading-tight tracking-tight tabular-nums">
        {formatUsd(data.totalUsd, locale)}
      </div>
      <div className="flex items-center gap-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-white/90">
        {t("topbar.total")}
        {/* On the TOTAL line, not the header: beside the address chip it
            squeezed the chip onto two lines at 360px. Dark text on amber:
            white on amber-400 is ~1.7:1. */}
        {isTestnet && (
          <span
            className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[0.625rem] font-bold tracking-wider text-amber-950"
            title={t("topbar.testnetHint")}
            data-testid="wallet-testnet-badge"
          >
            {t("network.testnetShort")}
          </span>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white/10 px-2.5 py-2 ring-1 ring-inset ring-white/15">
          <dt className="flex items-center gap-1.5 text-xs font-semibold text-white">
            <span aria-hidden className="h-2 w-2 rounded-full bg-[#26A17B] ring-1 ring-white/40" />
            USDT
          </dt>
          <dd className="mt-0.5 text-[0.9375rem] font-semibold tabular-nums">
            {data.usdt.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </dd>
        </div>
        <div className="rounded-lg bg-white/10 px-2.5 py-2 ring-1 ring-inset ring-white/15">
          <dt className="flex items-center gap-1.5 text-xs font-semibold text-white">
            <span aria-hidden className="h-2 w-2 rounded-full bg-[#A78BFA] ring-1 ring-white/40" />
            POL
          </dt>
          <dd className="mt-0.5 truncate text-[0.9375rem] font-semibold tabular-nums">
            {data.pol.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
          </dd>
          {/* Own line: squeezed beside the amount it truncated at 288px. */}
          {polUsd != null && (
            <dd className="text-xs tabular-nums text-white/90">≈ {formatUsd(polUsd, locale)}</dd>
          )}
        </div>
      </dl>
    </>
  );
}

// min-h-11 = 44px: the WCAG 2.5.5 touch target. focus-visible draws a ring for
// keyboard users only; pointer hover keeps the soft background.
const itemFocusClass =
  "outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500";

const menuItemClass =
  `flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-tertiary hover:text-text-primary focus:bg-surface-tertiary focus:text-text-primary ${itemFocusClass}`;

const primaryFundClass =
  "flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-white px-2 text-sm font-semibold text-brand-800 shadow-sm outline-none transition hover:bg-brand-50 focus:bg-brand-50 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-700 motion-safe:active:scale-[0.98]";

function AvatarDropdown({
  displayName,
  avatarUrl,
  walletAddress,
  checklist,
  onProfile,
  onLogout,
}: {
  displayName: string | null;
  avatarUrl?: string | null;
  walletAddress?: string | null;
  /** The worker checklist (useWorkerChecklist), or null once it's done. */
  checklist: Checklist | null;
  onProfile: () => void;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const showChecklist = checklist !== null;
  const { data: channels = [] } = useNotificationChannels();
  const telegramChannel = channels.find((c) => c.provider === "telegram" && !c.revoked_at);
  const activeChannels = getActiveChannels(channels);
  const { copied, copy, reset } = useCopyToClipboard({ resetMs: 2000 });
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [fundModalFlow, setFundModalFlow] = useState<Flow>("onramp");
  // Gated on the menu being open: TopBar mounts on every page, and minting a
  // link for someone who never opens the menu is churn on the token table.
  const [menuOpen, setMenuOpen] = useState(false);
  // The menu has two views: the account card, and the getting-started steps.
  // A mouse hovering the avatar opens the steps (Spotify's "Getting started"
  // panel) and leaving closes them; a click, a tap or the keyboard opens the
  // account card, which keeps one row leading to the steps.
  const [view, setView] = useState<"account" | "steps">("account");
  const [openedByHover, setOpenedByHover] = useState(false);
  // "steps" entered from the account card's row: offer the way back.
  const [stepsFromRow, setStepsFromRow] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const telegram = useTelegramHandoff({ enabled: menuOpen });
  // Same gate for the track record: fetched on the first open, then cached.
  const { data: profile, isPending: profilePending } = useProfile(undefined, { enabled: menuOpen });
  // The menu leads with the @handle — it's how people find each other here
  // (/u/:handle) and, unlike a full name, it fits the row untruncated. While
  // the profile loads a placeholder holds the line, so the name doesn't flash
  // in and get swapped out; a failed read falls back to the name.
  const handle = profile?.handle ? `@${profile.handle}` : null;
  // Radix returns focus to the avatar when the menu closes. When the close
  // came from Deposit/Withdraw the fund dialog is opening and owns focus.
  const fundOpeningRef = useRef(false);
  // Testnet money is play money: the card says so, and its primary action is
  // the faucet. A card on-ramp can't fund an Amoy wallet, so Deposit there was
  // a dead end for every admin and dev trying to set up a test.
  const isTestnet = !isMainnetChainId(useNetworkStore((s) => s.chainId));
  const testUsdt = useMintTestUsdt();

  function changeOpen(open: boolean) {
    clearTimeout(closeTimer.current);
    setMenuOpen(open);
    if (!open) {
      reset();
      setView("account");
      setOpenedByHover(false);
      setStepsFromRow(false);
    }
  }
  // Hover is for a mouse only: a tap also fires pointerenter, and must open
  // the account card like a click.
  function hoverIn(e: PointerEvent) {
    if (e.pointerType !== "mouse" || !showChecklist) return;
    clearTimeout(closeTimer.current);
    if (menuOpen) return;
    setView("steps");
    setOpenedByHover(true);
    setMenuOpen(true);
  }
  function hoverOut(e: PointerEvent) {
    // A menu the member clicked open stays open until they close it.
    if (e.pointerType !== "mouse" || !openedByHover) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => changeOpen(false), HOVER_CLOSE_MS);
  }

  function openFund(flow: Flow) {
    fundOpeningRef.current = true;
    setFundModalFlow(flow);
    setFundModalOpen(true);
  }

  function handleCopyAddress() {
    if (!walletAddress) return;
    copy(walletAddress);
  }

  return (
    // Not modal: a modal menu blocks pointer events outside it, and
    // hover-to-close needs them.
    <DropdownMenu.Root open={menuOpen} onOpenChange={changeOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          ref={triggerRef}
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          // Clicking the avatar while hover has the steps up switches to the
          // account card instead of toggling the menu shut (Radix skips its
          // handler when prevented).
          onPointerDown={(e) => {
            if (e.pointerType === "mouse" && menuOpen && openedByHover) {
              e.preventDefault();
              clearTimeout(closeTimer.current);
              setOpenedByHover(false);
              setView("account");
            }
          }}
          // The progress ring overhangs the avatar by 3.5px; a ring-offset-2
          // hover/open ring would paint over it and fill its segment gaps. `flex`
          // drops the inline line box that made the button 32×40 (an oval ring).
          className={`flex rounded-full outline-none ${showChecklist ? "ring-offset-[5px]" : "ring-offset-2"} ring-offset-surface transition-shadow hover:ring-2 hover:ring-brand-400/60 focus-visible:ring-2 focus-visible:ring-brand-500 data-[state=open]:ring-2 data-[state=open]:ring-brand-500`}
          aria-label={t("topbar.accountMenu")}
        >
          {checklist ? (
            <ProgressRing
              steps={checklist.steps.filter((s) => s.status !== "locked").map((s) => s.status === "done")}
              label={t("gettingStarted.ringLabel", { done: checklist.done, total: checklist.total })}
            >
              <Avatar name={displayName || "User"} src={avatarUrl} size="sm" />
            </ProgressRing>
          ) : (
            <Avatar name={displayName || "User"} src={avatarUrl} size="sm" />
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          data-testid="account-menu"
          data-view={view}
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          // The avatar sits outside the panel, so Radix would read the click
          // that swaps hover's steps for the account card as "outside" and
          // close the menu instead.
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
          className="z-50 w-[304px] max-w-[calc(100vw-16px)] rounded-2xl border border-border-default bg-surface p-2 shadow-2xl shadow-black/25"
          sideOffset={10}
          align="end"
          collisionPadding={8}
          onCloseAutoFocus={(e) => {
            if (fundOpeningRef.current) {
              e.preventDefault();
              fundOpeningRef.current = false;
            }
          }}
        >
          {view === "steps" && checklist ? (
            <GettingStartedSection
              checklist={checklist}
              onNavigate={(href) => navigate(href)}
              onBack={
                stepsFromRow
                  ? () => {
                      setStepsFromRow(false);
                      setView("account");
                    }
                  : undefined
              }
            />
          ) : (
            <>
            {checklist && (
              <GettingStartedRow
                checklist={checklist}
                onOpen={() => {
                  setStepsFromRow(true);
                  setView("steps");
                }}
              />
            )}
            {/* Identity header — the whole row opens the profile. */}
            <DropdownMenu.Item
              className={`group flex min-h-14 cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-surface-tertiary focus:bg-surface-tertiary ${itemFocusClass}`}
              onSelect={onProfile}
              data-testid="account-menu-identity"
            >
              <Avatar
                name={displayName || "User"}
                src={avatarUrl}
                size="md"
                className="ring-2 ring-brand-500/40 ring-offset-2 ring-offset-surface"
              />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  {profilePending ? (
                    <span
                      aria-hidden
                      className="h-4 w-28 rounded bg-surface-tertiary motion-safe:animate-pulse"
                      data-testid="account-menu-handle-loading"
                    />
                  ) : (
                    <span
                      className="truncate text-[0.9375rem] font-semibold tracking-tight text-text-primary"
                      data-testid="account-menu-handle"
                    >
                      {handle ?? displayName ?? t("topbar.profile")}
                    </span>
                  )}
                  {activeChannels.length > 0 && (
                    <span className="flex shrink-0 items-center gap-1" data-testid="active-channels">
                      {activeChannels.map(({ provider, labelKey, icon: Icon, iconColor, displayName: contact }) => {
                        const label = contact ? `${t(labelKey)} · ${contact}` : t(labelKey);
                        return (
                          <span
                            key={provider}
                            title={label}
                            className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-tertiary"
                          >
                            <Icon className={`h-3 w-3 ${iconColor}`} aria-hidden />
                            <span className="sr-only">{label}</span>
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-text-secondary">
                  <User className="h-3.5 w-3.5" aria-hidden />
                  {t("topbar.viewProfile")}
                </div>
              </div>
              <ChevronRight
                aria-hidden
                className="h-4 w-4 shrink-0 text-text-secondary transition-transform motion-safe:group-hover:translate-x-0.5 motion-safe:group-focus:translate-x-0.5"
              />
            </DropdownMenu.Item>

            {/* Linked but paused still counts as connected: the prompt is for
                members who never linked Telegram, not ones who muted it. */}
            {!telegramChannel && (
              // A link, not a trigger: the tap that chooses this item is the
              // same gesture that hands off to Telegram. Hidden until the link
              // exists so the item is never a dead tap.
              telegram.url && (
                <DropdownMenu.Item asChild>
                  {/* Telegram blue (#26A5E4) is ~2.9:1 on white; the text uses a
                      darker shade in light mode to clear 4.5:1. */}
                  <a
                    href={telegram.url}
                    {...(telegram.onPhone
                      ? {}
                      : { target: "_blank", rel: "noopener noreferrer" })}
                    onClick={telegram.markHandedOff}
                    className={`mx-1 mb-1.5 flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#26A5E4]/60 px-3 text-sm font-medium text-[#1A6E9C] transition-colors hover:bg-[#26A5E4]/10 focus:bg-[#26A5E4]/10 dark:text-[#5CBDF0] ${itemFocusClass}`}
                  >
                    <TelegramIcon className="h-4 w-4" aria-hidden />
                    {t("topbar.connectTelegram", "Connect Telegram")}
                  </a>
                </DropdownMenu.Item>
              )
            )}

            <div className="mx-1 mb-2">
              <TrackRecordMini profileId={menuOpen ? profile?.id : null} />
            </div>

            {walletAddress && (
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 p-4 text-white shadow-inner">
                {/* Decorative glow — purely visual. */}
                <div aria-hidden className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                <div aria-hidden className="pointer-events-none absolute -bottom-14 -left-8 h-28 w-28 rounded-full bg-fuchsia-400/15 blur-2xl" />

                <div className="relative">
                  <div className="flex items-center justify-between gap-2">
                    <DropdownMenu.Label className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-white">
                      <Wallet className="h-3.5 w-3.5" aria-hidden />
                      {t("topbar.balances")}
                    </DropdownMenu.Label>
                    <DropdownMenu.Item
                      className="flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full bg-black/25 px-2.5 font-mono text-[0.6875rem] text-white outline-none ring-1 ring-inset ring-white/20 transition-colors hover:bg-black/35 focus:bg-black/35 focus-visible:ring-2 focus-visible:ring-white"
                      aria-label={t(copied ? "topbar.addressCopied" : "topbar.copyAddress")}
                      title={walletAddress}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleCopyAddress();
                      }}
                    >
                      {walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-success-400" aria-hidden />
                      ) : (
                        <Copy className="h-3.5 w-3.5 opacity-80" aria-hidden />
                      )}
                    </DropdownMenu.Item>
                  </div>
                  {/* Announces the copy: the icon swap alone is silent. */}
                  <span role="status" className="sr-only">
                    {copied ? t("topbar.addressCopied") : ""}
                  </span>

                  <BalanceSummary isTestnet={isTestnet} />

                  {/* Menu items, not bare buttons: Radix menus move focus with
                      the arrow keys between items only, so plain buttons here
                      were unreachable from the keyboard. */}
                  <div className="mt-3 flex gap-2">
                    {testUsdt.available ? (
                      // Stays open (preventDefault) so the balance above is seen
                      // ticking up when the mint lands.
                      <DropdownMenu.Item
                        onSelect={(e) => {
                          e.preventDefault();
                          testUsdt.mint();
                        }}
                        disabled={testUsdt.minting}
                        className={`${primaryFundClass} whitespace-nowrap data-[disabled]:cursor-wait data-[disabled]:opacity-80`}
                        // The visible label is the short "+1,000 USDT" (the long
                        // one wrapped in a half-width button); the name says it.
                        aria-label={t("testnet.getTestUsdtLong")}
                        title={t("testnet.getTestUsdtLong")}
                        data-testid="mint-test-usdt"
                      >
                        {testUsdt.minting ? (
                          <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden />
                        ) : (
                          <Coins className="h-4 w-4" aria-hidden />
                        )}
                        {t(testUsdt.minting ? "testnet.minting" : "testnet.getTestUsdtShort")}
                      </DropdownMenu.Item>
                    ) : (
                      <DropdownMenu.Item
                        onSelect={() => openFund("onramp")}
                        className={primaryFundClass}
                      >
                        <CreditCard className="h-4 w-4" aria-hidden />
                        {t("topbar.deposit", "Deposit")}
                      </DropdownMenu.Item>
                    )}
                    <DropdownMenu.Item
                      onSelect={() => openFund("offramp")}
                      className="flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-white/5 px-2 text-sm font-semibold text-white outline-none ring-1 ring-inset ring-white/50 transition hover:bg-white/20 focus:bg-white/20 focus-visible:ring-2 focus-visible:ring-white motion-safe:active:scale-[0.98]"
                    >
                      <ArrowUpFromLine className="h-4 w-4" aria-hidden />
                      {t("topbar.withdraw", "Withdraw")}
                    </DropdownMenu.Item>
                  </div>
                </div>
              </div>
            )}

            <DropdownMenu.Separator className="my-2 h-px bg-border-subtle" />
            {/* Organizations live on /profile (my orgs, invites, create). */}
            <DropdownMenu.Item
              onSelect={() => navigate(ROUTES.PROFILE)}
              className={menuItemClass}
            >
              <Building2 className="h-4 w-4" aria-hidden />
              {t("orgs.myOrgs")}
            </DropdownMenu.Item>
            {/* The admin dashboard lives here on phones rather than as a sixth
                bottom-nav tab, which crowded every label for admins. */}
            {isAdmin && (
              <DropdownMenu.Item
                onSelect={() => navigate(ROUTES.ADMIN)}
                className={menuItemClass}
              >
                <Shield className="h-4 w-4" aria-hidden />
                {t("nav.admin")}
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item asChild>
              <a
                href={FEEDBACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={menuItemClass}
              >
                <Lightbulb className="h-4 w-4" aria-hidden />
                {t("nav.feedback")}
              </a>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm font-medium text-red-800 transition-colors hover:bg-error-500/10 focus:bg-error-500/10 dark:text-red-300 ${itemFocusClass}`}
              onSelect={onLogout}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              {t("topbar.signOut")}
            </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
      <FundWalletModal
        open={fundModalOpen}
        onOpenChange={setFundModalOpen}
        walletAddress={walletAddress ?? ""}
        defaultFlow={fundModalFlow}
      />
    </DropdownMenu.Root>
  );
}

function TopBar() {
  const { t } = useTranslation();
  // Worker steps come first: while they're up, the org checklist pill waits.
  const workerChecklist = useWorkerChecklist();
  const { displayName, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const firstName = displayName?.split(" ")[0] ?? "there";

  // DEV-862: the greeting needs `min-w-0` or flexbox holds it at its
  // min-content width, and the surplus turns into extra *lines* inside a
  // fixed h-14 -- on a 360px phone "Hola, Cecilia 👋" rendered as three rows
  // spilling above and below the bar. `shrink-0` on the control cluster is
  // the other half: without it flex shrank the controls too and pushed the
  // avatar off the right edge. Together they also keep the header's
  // scrollWidth inside the viewport, which is what was making the page
  // pannable/zoomable on mobile.
  return (
    <header className="flex h-14 items-center gap-2 border-b border-border-default bg-surface px-3 sm:px-4 md:px-6">
      {/* TopBar only renders inside AppShell, i.e. for a signed-in person. */}
      <ShellBackButton isAuthenticated className="-ml-1.5 sm:-ml-2" />
      <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary sm:text-lg">
        {/* Phones show the name alone: the greeting word is the least
            meaningful part of the line, so it is what gives way rather than
            the tail of someone's name. */}
        <span className="sm:hidden">
          {t("topbar.greetingShort", { name: firstName })}
        </span>
        <span className="hidden sm:inline">
          {t("topbar.greeting", { name: firstName })}
        </span>{" "}
        <span aria-hidden="true">👋</span>
      </h2>
      <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
        {/* Chain switch is an admin-only affordance -- it repoints the admin
            data views between Amoy and Polygon. */}
        {isAdmin && <NetworkSwitcher compact />}
        <LanguageSwitcher />
        <ThemeToggle className="p-1.5 sm:p-2" />
        {/* Search is inert chrome for now, so it is the one control here not
            worth its width on a phone -- Discover in the bottom nav covers
            finding things until this is wired up. */}
        <button
          className="hidden rounded-full p-2 text-text-tertiary hover:bg-surface-tertiary hover:text-text-secondary sm:block"
          aria-label={t("topbar.search")}
        >
          <Search className="h-5 w-5" />
        </button>
        <button
          onClick={() => navigate(ROUTES.NOTIFICATIONS)}
          className="relative rounded-full p-1.5 text-text-muted hover:bg-surface-tertiary hover:text-text-secondary sm:p-2"
          aria-label={t("topbar.notifications")}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
        {/* Spotify-style pill: the org checklist ring + the avatar; hovering
            either opens the checklist. Just the avatar once it's done or
            hidden, and while the worker's own steps are still up. */}
        <OrgChecklistPill suppressed={workerChecklist !== null}>
          {useMockAuth ? <MockAvatarMenu checklist={workerChecklist} /> : <PrivyAvatarMenu checklist={workerChecklist} />}
        </OrgChecklistPill>
      </div>
    </header>
  );
}

export { TopBar };
